import React, { useEffect, useState, useCallback } from "react";
import { Trophy, Crown, RefreshCw, Flame, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { flagEmoji } from "@/lib/country";
import TradersByCountry from "@/components/TradersByCountry";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Home-screen weekly leaderboard — ranks every trader by NET PROFIT across the
// WHOLE platform (Pro Trade + Matches/Differs + Forex bot + Digits + manual).
// Resets every Monday; the weekly #1 is auto-posted to the Telegram channel.
export const LeaderboardCard = ({ accountId }) => {
  const [rowsDaily, setRowsDaily] = useState([]);
  const [rowsWeekly, setRowsWeekly] = useState([]);
  const [scope, setScope] = useState("daily");
  const [champ, setChamp] = useState(null);
  const [week, setWeek] = useState("");
  const [today, setToday] = useState(null);
  const [myWeek, setMyWeek] = useState(null);
  const [type, setType] = useState("real");
  const [loading, setLoading] = useState(true);
  const [myName, setMyName] = useState(null);
  const [nickOpen, setNickOpen] = useState(false);
  const [nick, setNick] = useState("");
  const [savingNick, setSavingNick] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const me = accountId ? `&me=${encodeURIComponent(accountId)}` : "";
      const [lbD, lbW, wt, msD, msW, dn] = await Promise.all([
        fetch(`${BACKEND}/api/pro-trade/leaderboard?scope=daily&account_type=${type}&limit=5${me}`).then((r) => r.json()),
        fetch(`${BACKEND}/api/pro-trade/leaderboard?scope=weekly&account_type=${type}&limit=5${me}`).then((r) => r.json()),
        fetch(`${BACKEND}/api/pro-trade/weekly-top?account_type=${type}`).then((r) => r.json()),
        accountId ? fetch(`${BACKEND}/api/pro-trade/my-stats?account_id=${encodeURIComponent(accountId)}&scope=daily`).then((r) => r.json()) : Promise.resolve(null),
        accountId ? fetch(`${BACKEND}/api/pro-trade/my-stats?account_id=${encodeURIComponent(accountId)}&scope=weekly`).then((r) => r.json()) : Promise.resolve(null),
        accountId ? fetch(`${BACKEND}/api/pro-trade/display-name/${encodeURIComponent(accountId)}`).then((r) => r.json()) : Promise.resolve(null),
      ]);
      setRowsDaily(lbD?.leaders || []);
      setRowsWeekly(lbW?.leaders || []);
      setChamp(wt?.top || null);
      setWeek(wt?.week || "");
      setToday(msD || null);
      setMyWeek(msW || null);
      setMyName(dn?.display_name || null);
    } catch (_e) {
      setRowsDaily([]); setRowsWeekly([]); setChamp(null); setToday(null); setMyWeek(null);
    }
    setLoading(false);
  }, [type, accountId]);

  const saveNick = async () => {
    const name = nick.trim();
    if (name.length < 3 || name.length > 20) { toast.error("Nickname must be 3–20 characters"); return; }
    setSavingNick(true);
    try {
      const res = await fetch(`${BACKEND}/api/pro-trade/display-name`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: accountId, display_name: name }),
      });
      const d = await res.json();
      if (!res.ok) { toast.error(d?.detail || "Could not save nickname"); }
      else { setMyName(d.display_name); setNickOpen(false); toast.success(`You're now "${d.display_name}" on the leaderboard`); load(); }
    } catch (_e) { toast.error("Network error — try again"); }
    setSavingNick(false);
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const money = (n) => `${n >= 0 ? "+" : "-"}$${Math.abs(Number(n) || 0).toFixed(2)}`;
  const medal = (rank) => (rank === 1 ? "text-amber-400" : rank === 2 ? "text-slate-300" : rank === 3 ? "text-amber-600" : "text-vts-silver/40");
  const rows = scope === "weekly" ? rowsWeekly : rowsDaily;
  const myStats = scope === "weekly" ? myWeek : today;
  const dayLeader = rowsDaily[0]?.account || null;
  const weekLeader = rowsWeekly[0]?.account || champ?.account || null;
  const doubleLeader = dayLeader && weekLeader && dayLeader === weekLeader;

  return (
    <div data-testid="home-leaderboard" className="rounded-xl border border-vts-line bg-vts-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-vts-line bg-gradient-to-r from-amber-400/10 to-transparent">
        <Trophy className="h-4 w-4 text-amber-400 shrink-0" />
        <div className="leading-tight">
          <p className="text-sm font-semibold text-white">Leaderboard</p>
          <p className="text-[10px] text-vts-silver/50">
            {scope === "weekly" ? "top 5 this week · resets Monday 00:00 UTC" : "top 5 in profit today · resets daily at 00:00 UTC"}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <div className="flex gap-0.5 rounded-md border border-vts-line p-0.5">
            {[["daily", "Today"], ["weekly", "Week"]].map(([k, l]) => (
              <button
                key={k}
                data-testid={`home-lb-scope-${k}`}
                onClick={() => setScope(k)}
                className={`px-2 py-0.5 rounded text-[10px] font-bold transition ${scope === k ? "bg-vts-blue/25 text-vts-blue" : "text-vts-silver/50 hover:text-white"}`}
              >
                {l}
              </button>
            ))}
          </div>
          <div className="flex gap-0.5 rounded-md border border-vts-line p-0.5">
            {[["real", "Real"], ["demo", "Demo"]].map(([k, l]) => (
              <button
                key={k}
                data-testid={`home-lb-type-${k}`}
                onClick={() => setType(k)}
                className={`px-2 py-0.5 rounded text-[10px] font-bold transition ${type === k ? (k === "real" ? "bg-vts-profit/25 text-vts-profit" : "bg-amber-500/20 text-amber-400") : "text-vts-silver/50 hover:text-white"}`}
              >
                {l}
              </button>
            ))}
          </div>
          <button data-testid="home-lb-refresh" onClick={load} className="p-1.5 rounded-md hover:bg-vts-surface transition" title="Refresh">
            <RefreshCw className={`h-3.5 w-3.5 text-vts-silver/55 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Your net profit (wins AND losses netted) — follows the Today/Week toggle */}
      {accountId && myStats && (
        <div data-testid="home-lb-today" className="flex items-center gap-2 px-4 py-2.5 border-b border-vts-line bg-vts-surface/40">
          <span className="text-[10px] uppercase tracking-wider text-vts-silver/50">Your profit {scope === "weekly" ? "this week" : "today"}</span>
          <span data-testid="home-lb-today-pnl" className={`ml-auto text-[15px] font-bold font-mono ${myStats.pnl >= 0 ? "text-vts-profit" : "text-vts-loss"}`}>{money(myStats.pnl)}</span>
          <span className="text-[10px] text-vts-silver/45">net · {myStats.wins}W / {myStats.losses}L</span>
        </div>
      )}

      <div className="px-4 pt-2.5">
        {/* only the flags of traders who actually made the board — keeps the row clean */}
        <TradersByCountry scope={scope === "weekly" ? "weekly" : "daily"} only={rows.map((r) => r.country)} />
      </div>

      {/* Champion banner */}
      {champ && (
        <div data-testid="home-lb-champion" className="flex items-center gap-3 px-4 py-3 border-b border-vts-line bg-gradient-to-br from-amber-400/10 to-vts-blue/5">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-400/15 border border-amber-400/30 shrink-0">
            <Crown className="h-4.5 w-4.5 text-amber-400" />
          </div>
          <div className="min-w-0">
            <p className="text-[9px] uppercase tracking-wider text-amber-400 font-bold">This week's champion{champ.is_demo ? " · Demo" : ""}</p>
            <p className="text-[15px] font-bold text-white truncate">{champ.country ? `${flagEmoji(champ.country)} ` : ""}{champ.account}</p>
            {doubleLeader && champ.account === weekLeader ? (
              <p data-testid="home-lb-double-leader" className="text-[9px] text-vts-profit font-bold">🔥 also leading TODAY — double crown</p>
            ) : dayLeader && (
              <p data-testid="home-lb-day-leader" className="text-[9px] text-vts-silver/55">today's leader: <span className="text-white font-semibold">{dayLeader}</span></p>
            )}
          </div>
          <div className="ml-auto text-right shrink-0">
            <p className={`text-[15px] font-bold font-mono ${champ.pnl >= 0 ? "text-vts-profit" : "text-vts-loss"}`}>{money(champ.pnl)}</p>
            <p className="text-[9px] text-vts-silver/50">{champ.win_rate}% · {champ.trades} trade{champ.trades === 1 ? "" : "s"}</p>
          </div>
        </div>
      )}

      {/* Rows */}
      <div className="divide-y divide-vts-line/60">
        {rows.length === 0 && !loading && (
          <div data-testid="home-lb-empty" className="flex flex-col items-center gap-1.5 px-4 py-7 text-center">
            <Flame className="h-5 w-5 text-vts-blue/70" />
            <p className="text-sm text-white font-medium">No one's in profit yet {scope === "weekly" ? "this week" : "today"}</p>
            <p className="text-[11px] text-vts-silver/50">Bank a winning trade to claim the top spot — only profitable traders make the board. 🏆</p>
          </div>
        )}
        {rows.map((r) => (
          <div
            key={r.rank}
            data-testid={`home-lb-row-${r.rank}`}
            className={`flex items-center gap-3 px-4 py-2.5 ${r.is_me ? "bg-vts-blue/10" : ""}`}
          >
            <span className={`w-5 text-center text-[13px] font-bold font-mono ${medal(r.rank)}`}>{r.rank}</span>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] text-white font-medium truncate flex items-center gap-1.5">
                {r.country && <span data-testid={`home-lb-flag-${r.rank}`} className="text-[13px] leading-none">{flagEmoji(r.country)}</span>}
                {r.account}
                {r.is_me && <span className="text-[8px] font-bold uppercase px-1 rounded bg-vts-blue/30 text-vts-blue">You</span>}
                {r.is_demo && <span className="text-[7px] font-mono text-vts-silver/45 px-1 rounded bg-vts-line">DEMO</span>}
              </p>
              <p className="text-[9px] text-vts-silver/45">{r.win_rate}% win · {r.trades} trade{r.trades === 1 ? "" : "s"}</p>
            </div>
            <span className={`text-[13px] font-bold font-mono shrink-0 ${r.pnl >= 0 ? "text-vts-profit" : "text-vts-loss"}`}>{money(r.pnl)}</span>
          </div>
        ))}
      </div>

      {/* Nickname editor — claim a public leaderboard name */}
      {accountId && (
        <div data-testid="home-lb-nickname" className="border-t border-vts-line px-4 py-2.5 bg-vts-surface/30">
          {!nickOpen ? (
            <button
              data-testid="home-lb-nickname-edit"
              onClick={() => { setNick(myName || ""); setNickOpen(true); }}
              className="flex w-full items-center gap-2 text-left"
            >
              <Pencil className="h-3.5 w-3.5 text-vts-blue shrink-0" />
              <span className="text-[11px] text-vts-silver/60">Leaderboard name:</span>
              <span className="text-[12px] font-semibold text-white">{myName || "Set a nickname"}</span>
              <span className="ml-auto text-[10px] text-vts-blue">{myName ? "Edit" : "Set →"}</span>
            </button>
          ) : (
            <div className="flex items-center gap-1.5">
              <input
                data-testid="home-lb-nickname-input"
                value={nick}
                onChange={(e) => setNick(e.target.value)}
                maxLength={20}
                autoFocus
                placeholder="3–20 chars (a-z, 0-9, _ -)"
                onKeyDown={(e) => e.key === "Enter" && saveNick()}
                className="flex-1 rounded-md border border-vts-line bg-vts-bg px-2.5 py-1.5 text-[12px] text-white outline-none focus:border-vts-blue/60"
              />
              <button data-testid="home-lb-nickname-save" disabled={savingNick} onClick={saveNick} className="rounded-md bg-vts-blue/20 border border-vts-blue/50 p-1.5 text-vts-blue hover:bg-vts-blue/30 disabled:opacity-50">
                <Check className="h-4 w-4" />
              </button>
              <button data-testid="home-lb-nickname-cancel" onClick={() => setNickOpen(false)} className="rounded-md border border-vts-line p-1.5 text-vts-silver/60 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LeaderboardCard;
